<template>
  <div
    class="form-group relative"
    :class="inputWrapClasses"
  >
    <input
      v-if="inputType !== 'prefix'"
      :id="inputId"
      v-tooltip.top-end="{
        theme: tooltipHint && !isError ? 'inputTooltipInfo' : 'inputTooltip',
        content: tooltipHint && !isError ? tooltipHint : errorMessage(),
        shown: !hideTooltip,
        triggers: [],
      }"
      :value="modelValue"
      :type="inputType === 'password' ? (showPassword === true ? 'text' : 'password') : inputType"
      class="form-control form-control-lg rounded-sm shadow-none"
      :class="[inputClasses, { 'input-password-pr': inputType === 'password'}]"
      :inputmode="inputMode || null"
      :autocomplete="autoComplete"
      :name="inputId"
      :aria-label="ariaLabel || null"
      :required="required"
      :pattern="InputValidation.pattern(country, inputType === 'password' ? 'password' : inputValidation) || null"
      :maxlength="maxLength || null"
      :minlength="minLength || null"
      :max="maxNumber || null"
      :min="minNumber || null"
      :readonly="readOnly || null"
      placeholder=" "
      @focus="onFocusHandler"
      @input="onInputHandler"
      @change="emit('onChange')"
      @blur="onBlurHandler"
      @keyup="onKeyUpChange"
      @invalid="hideTooltip = false"
    >
    <select
      v-else
      :id="inputId"
      :value="modelValue"
      :name="inputId"
      class="custom-select custom-select-lg rounded-sm shadow-none"
      :class="[inputClasses]"
      dir="rtl"
      :aria-label="ariaLabel || null"
      :required="required"
      @change="onInputHandler"
      @blur="onBlurHandler"
      @invalid="hideTooltip = false"
    >
      <option
        dir="ltr"
        selected
        disabled
        label=" "
      />
      <option
        v-for="phonePrefix in (isCzGlsBoxes ? ['+420'] : defaultPhonePrefixes(phonePrefixMode === 'all' ? 'all' : country))"
        :key="phonePrefix"
        dir="ltr"
        :value="phonePrefix"
      >
        {{ phonePrefix }}
      </option>
    </select>
    <label
      v-if="inputLabel.length > 0"
      :for="inputId"
      class="left-0 label-animated absolute centered-vertical text-muted bg-white rounded-sm"
      :class="[labelSpacing ? labelSpacing : 'px-2 m-0 ml-2' , { 'label-active': inputFocused === true || (modelValue && modelValue.length > 0) }]"
    >
      {{ inputLabel }}
      <span
        v-if="required"
        class="text-danger"
      >*</span>
    </label>
    <span
      v-if="false"
      :id="`${inputId}Helper`"
      class="placeholder-helper events-none absolute form-control-lg px-4 m-0 text-muted border-1"
      :class="[
        { 'helper-animated': inputLabel.length > 0 },
        { 'helper-active': inputLabel.length > 0 && inputFocused === true },
      ]"
    >
      {{ inputHelper }}
    </span>
    <button
      v-if="inputType === 'password'"
      type="button"
      class="absolute right-0 bg-transparent border-0 outline-none toggle-password-visibility mr-2"
      @click="showPassword = !showPassword"
    >
      <i
        class="icon-24 text-primary"
        :class="showPassword ? 'mi-pass-show' : 'mi-pass-hide'"
      />
    </button>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'

import { vTooltip } from 'floating-vue';
import { useI18n } from 'vue-i18n';
import { useDefaultPhonePrefixes } from '@/mixins/defaultPhonePrefixFinder';
import { useCountry } from '@/mixins/useCountry'

import InputValidation from '@/services/inputValidation'

const { t, te } = useI18n();

const country = useCountry()

const props = defineProps({
  modelValue: { type: String, default: '' },
  inputId: { type: String, required: true },
  inputType: { type: String, required: true },
  required: { type: Boolean, default: false },
  inputWrapClasses: { type: String, default: '' },
  inputClasses: { type: String, default: '' },
  labelSpacing: { type: String, default: '' },
  inputPattern: { type: String, default: '.*' },
  inputLabel: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  inputMode: { type: String, default: '' },
  autoComplete: { type: String, default: '' },
  inputHelper: { type: String, default: '' },
  readOnly: { type: Boolean, default: false },
  inputValidation: { type: String, default: '' },
  tooltipHint: { type: String, default: '' },
  // pattern: { type: String, default: '' },
  // allowedBaseChars: { type: Array, default: () => [] },
  // allowedInputChars: { type: Array, default: () => [] },
  maxLength: { type: [String, Number], default: '' },
  minLength: { type: [String, Number], default: '' },
  maxNumber: { type: [String, Number], default: '' },
  minNumber: { type: [String, Number], default: '' },
  phonePrefixMode: { type: String, default: 'auto' },
  isCzGlsBoxes: { type: Boolean, default: false },
})

const defaultPhonePrefixes = useDefaultPhonePrefixes;

const inputFocused = ref(false)
const showPassword = ref(false)
const hideTooltip = ref(true)
const isError = ref(false)

const errorMessage = () => {
  if (props.inputType === 'password') {
    return `${t('passwordMsg')}`
  } else if (props.inputValidation === 'addressNote') {
    return t('pleaseNoSpecialChars')
  } else if (props.modelValue === '' && props.required) {
    return `${t('fillRightGeneral')}`
  } else if (country === 'at' && props.inputValidation === 'psc') {
    return `${t('please_enter_the_correct_format')}`
  } else if (props.inputValidation) {
    return `${t('pleaseFill')} ${t(`${props.inputValidation}FillRight`)}${te(`${props.inputValidation}Example`) === true || props.inputValidation !== 'email' ? ` (${t('example')} ${t(`${props.inputValidation}Example`)})` : ''}`
  } else {
    return t('pleaseFillRight')
  }
}

// const allowedCharsJoined = new Set([...props.allowedBaseChars, ...props.allowedInputChars])

// const prefillPlaceholder = ref(props.inputHelper)

/*
span condition: v-if="inputHelper.length > 0"

@input="onInputHandler"
  @keypress="onKeypressHandler"
  @paste.prevent="onPasteHandler"
*/

const onFocusHandler = () => {
  //console.log('focused')
  if (props.tooltipHint !== '') {
    hideTooltip.value = false
  }
}

const onBlurHandler = (event) => {
  event.target.value.length > 0 ? (inputFocused.value = true) : (inputFocused.value = false)
  if (props.tooltipHint !== '') {
    hideTooltip.value = true
  }
}

const emit = defineEmits(['onChange', 'update:modelValue'])

onMounted(() => (props.modelValue ? (inputFocused.value = true) : (inputFocused.value = false)))

const onInputHandler = (event) => {
  if (!event.target.matches(':invalid:not(fieldset)')) {
    hideTooltip.value = true;
    isError.value = false;
  } else {
    hideTooltip.value = false;
    isError.value = true;
  }
  /* if (props.pattern.length > 0 && (props.allowedBaseChars.length > 0 || props.allowedInputChars.length > 0)) {
    patternCheck(event, props.allowedInputChars, props.allowedBaseChars, props.pattern)
  }
  if (props.inputHelper.length > 0) {
    updateHelperPlaceholder(event.target.value)
  } */
  emit('update:modelValue', event.target.value)
}

const onKeyUpChange = (event) => {
  if (props.inputType === 'number') {
    const inputNumber = parseInt(event.target.value)
    if (props.minNumber && inputNumber < parseInt(props.minNumber) ) {
      event.target.value = props.minNumber
      emit('update:modelValue', event.target.value)
    }
    if (props.maxNumber && inputNumber > parseInt(props.maxNumber) ) {
      event.target.value = props.maxNumber
      emit('update:modelValue', event.target.value)
    }
  }
}

/*
const updateHelperPlaceholder = (inputValue) => {
  prefillPlaceholder.value = props.inputHelper.slice(inputValue.length)

  document.querySelector(`#${props.inputId}Helper`).innerHTML =
    inputValue.length > 0
      ? `<span class="text-primary">${inputValue}</span>${prefillPlaceholder.value}`
      : props.inputHelper
}

const onKeypressHandler = (event) => {
  if (props.allowedBaseChars.length > 0 || props.allowedInputChars.length > 0) {
    checkPressedChar(event, allowedCharsJoined)
  }
}
const onPasteHandler = (event) => {
  if (props.pattern.length > 0 && props.allowedBaseChars.length > 0) {
    filterCopyPaste(event, allowedCharsJoined, props.pattern, defaultPhonePrefixes(locale.value))
    updateHelperPlaceholder(event.target.value)
  }
} */
</script>


<style lang="scss">
.label-animated {
  transition: .1s ease-in-out;
  transform: translateY(-50%) scale(1) translateX(0px);
  transform-origin: left;

  &.label-active {
    transform: translateY(-36px) scale(0.8) translateX(4px);
  }
}
.form-control:focus,
.form-control:not(:placeholder-shown) {
  ~ .label-animated {
    transform: translateY(-36px) scale(0.8) translateX(4px);
  }
}

.v-popper--theme-inputTooltip {
  background-color: $danger;
  color: $white;
  padding: 4px 16px;
  border-radius: 6px;
  font-size: 12px;

  .v-popper__arrow-container {
    left: auto !important;
    right: 16px;
    .v-popper__arrow-outer {
			border-color: $danger;
    }
  }
}

.v-popper--theme-inputTooltipInfo {
  background-color: $info;
  color: $white;
  padding: 4px 16px;
  border-radius: 6px;
  font-size: 12px;

  .v-popper__arrow-container {
    left: auto !important;
    right: 16px;
    .v-popper__arrow-outer {
			border-color: $info;
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "choose": "Vyberte",
    "customerNrExample": "123456",
    "customerNrFillRight": "musí mít 6 až 10 číslic",
    "dicExample": "CZ27767680",
    "dicFillRight": "je kód země a 10 číslic",
    "emailFillRight": "obsahuje {'@'} a .cz/.com atd.",
    "example": "např.:",
    "fillRightGeneral": "Vyplňte prosím toto pole",
    "icoExample": "12345678",
    "icoFillRight": "je 8 číslic",
    "passwordMsg": "Heslo musí mít alespoň 8 znaků",
    "phoneExample": "123 456 789",
    "phoneFillRight": "je 9 číslic",
    "pleaseFill": "Správný tvar",
    "pleaseFillRight": "Prosím, zadejte správný tvar",
    "pleaseNoSpecialChars": "Bez speciálních znaků",
    "please_enter_the_correct_format": "Zadejte prosím správný formát",
    "pscExample": "123 45",
    "pscFillRight": "je 5 číslic",
    "streetExample": "Ulice 123",
    "streetFillRight": "je název ulice + číslo domu",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "je 10 číslic"
},
    "sk": {
    "choose": "Vyberte",
    "customerNrExample": "123456",
    "customerNrFillRight": "musí mať 6 až 10 číslic",
    "dicExample": "SK27767680",
    "dicFillRight": "je kód krajiny a 8 číslic",
    "emailFillRight": "obsahuje {'@'} a .sk/.com atď.",
    "example": "napr.:",
    "fillRightGeneral": "Vyplňte prosím toto pole",
    "icoExample": "12345678",
    "icoFillRight": "je 9 číslic",
    "passwordMsg": "Heslo musí mať aspoň 8 znakov",
    "phoneExample": "123 456 789",
    "phoneFillRight": "je 9 - 10 číslic",
    "pleaseFill": "Správny tvar",
    "pleaseFillRight": "Prosím, zadajte správny tvar",
    "pleaseNoSpecialChars": "Bez špeciálnych znakov",
    "please_enter_the_correct_format": "Zadajte správny formát",
    "pscExample": "123 45",
    "pscFillRight": "je 5 číslic",
    "streetExample": "Ulica 123",
    "streetFillRight": "je názov ulice + číslo domu",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "je 10 číslic"
},
    "pl": {
    "choose": "Wybierz",
    "customerNrExample": "123456",
    "customerNrFillRight": "musi mieć od 6 do 10 cyfr",
    "dicExample": "PL123-456-32-18",
    "dicFillRight": "to kod kraju i 8 cyfr",
    "emailFillRight": "zawiera {'@'} a .pl/.com itp.",
    "example": "np:",
    "fillRightGeneral": "Podaj",
    "icoExample": "123456789101",
    "icoFillRight": "to 9 cyfr",
    "passwordMsg": "Hasło musi składać się z co najmniej 8 znaków",
    "phoneExample": "123 456 789",
    "phoneFillRight": "to 9 cyfr",
    "pleaseFill": "Prawidłowy format",
    "pleaseFillRight": "Wprowadź prawidłowy format",
    "pleaseNoSpecialChars": "Bez znaków specjalnych",
    "please_enter_the_correct_format": "Wprowadź prawidłowy format",
    "pscExample": "99-999",
    "pscFillRight": "to 6 znaków",
    "streetExample": "Ulica 12/34",
    "streetFillRight": "nazwę ulicy + numer domu/mieszkania",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "to 10 cyfr"
},
    "hu": {
    "choose": "Válassza ki",
    "customerNrExample": "123456",
    "customerNrFillRight": "6-10 számjegyűnek kell lennie",
    "dicExample": "CZ27767680",
    "dicFillRight": "ország kódja és 8 számjegy",
    "emailFillRight": "tartalmazza a {'@'} .cz/.com stb.",
    "example": "pl.:",
    "fillRightGeneral": "Kérjük, töltse ki ezt a mezőt",
    "icoExample": "12345678",
    "icoFillRight": "9 számjegy",
    "passwordMsg": "A jelszónak legalább 8 karakterből kell állnia",
    "phoneExample": "123 456 789",
    "phoneFillRight": "9 számjegy",
    "pleaseFill": "Helyes formátum",
    "pleaseFillRight": "Kérjük, írja be a helyes formátumot!",
    "pleaseNoSpecialChars": "Speciális karakterek nélkül",
    "please_enter_the_correct_format": "Kérjük, adja meg a helyes formátumot",
    "pscExample": "123 45",
    "pscFillRight": "5 számjegy",
    "streetExample": "Utca 123",
    "streetFillRight": "utca név + házszám",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "10 számjegyű"
},
    "de": {
    "choose": "Auswählen",
    "customerNrExample": "123456",
    "customerNrFillRight": "muss aus 6 bis 10 Ziffern bestehen",
    "dicExample": "CZ27767680",
    "dicFillRight": "ist die Landesvorwahl und 8 Ziffern",
    "emailFillRight": "{'@'} umfasst und .de/.com usw.",
    "example": "z.B.:",
    "fillRightGeneral": "Bitte fülle dieses Feld aus",
    "icoExample": "12345678",
    "icoFillRight": "sind 9 Ziffern",
    "passwordMsg": "Das Passwort muss mindestens 8 Zeichen lang sein.",
    "phoneExample": "123 456 789",
    "phoneFillRight": "sind 9 Ziffern",
    "pleaseFill": "Korrekte Form",
    "pleaseFillRight": "Bitte gebe die korrekte Form ein",
    "pleaseNoSpecialChars": "Ohne Sonderzeichen",
    "please_enter_the_correct_format": "Bitte gib das richtige Format ein",
    "pscExample": "12 345",
    "pscFillRight": "ist 5-stellig",
    "streetExample": "Straße 123",
    "streetFillRight": "ist der Straßenname + Hausnummer",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "ist 10 Ziffern"
},
    "at": {
    "choose": "Auswählen",
    "customerNrExample": "123456",
    "customerNrFillRight": "muss aus 6 bis 10 Ziffern bestehen",
    "dicExample": "CZ27767680",
    "dicFillRight": "ist die Landesvorwahl und 8 Ziffern",
    "emailFillRight": "{'@'} umfasst und .de/.com usw.",
    "example": "z.B.:",
    "fillRightGeneral": "Bitte fülle dieses Feld aus",
    "icoExample": "12345678",
    "icoFillRight": "sind 9 Ziffern",
    "passwordMsg": "Das Passwort muss mindestens 8 Zeichen lang sein.",
    "phoneExample": "123 456 789",
    "phoneFillRight": "sind 9 Ziffern",
    "pleaseFill": "Korrekte Form",
    "pleaseFillRight": "Bitte gebe die korrekte Form ein",
    "pleaseNoSpecialChars": "Ohne Sonderzeichen",
    "please_enter_the_correct_format": "Bitte gib das richtige Format ein",
    "pscExample": "12 345",
    "pscFillRight": "ist 5-stellig",
    "streetExample": "Straße 123",
    "streetFillRight": "ist der Straßenname + Hausnummer",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "ist 10 Ziffern"
},
    "ro": {
    "choose": "Selectați",
    "customerNrExample": "123456",
    "customerNrFillRight": "trebuie să aibă între 6 și 10 cifre",
    "dicExample": "RO27767680",
    "dicFillRight": "este codul țării și 8 cifre",
    "emailFillRight": "conţine{'@'} și .ro/.com etc.",
    "example": "de exemplu:",
    "fillRightGeneral": "Vă rugăm să completați acest câmp",
    "icoExample": "12345678",
    "icoFillRight": "este de 9 cifre",
    "passwordMsg": "Parola trebuie să aibă cel puțin 8 caractere",
    "phoneExample": "123 456 789",
    "phoneFillRight": "este de 9 cifre",
    "pleaseFill": "Forma corectă",
    "pleaseFillRight": "Vă rugăm să introduceți forma corectă",
    "pleaseNoSpecialChars": "Fără caractere speciale",
    "please_enter_the_correct_format": "Vă rugăm să introduceți formatul corect",
    "pscExample": "123 456",
    "pscFillRight": "este de 6 cifre",
    "streetExample": "Strada 123",
    "streetFillRight": "este numele străzii + numărul clădirii",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "este de 10 cifre"
},
    "fr": {
    "choose": "Sélectionnez",
    "customerNrExample": "123456",
    "customerNrFillRight": "doit contenir entre 6 et 10 chiffres",
    "dicExample": "CZ27767680",
    "dicFillRight": "est le code du pays et 10 chiffres",
    "emailFillRight": "contient un {'@'} .cz/.com, etc.",
    "example": "par exemple :",
    "fillRightGeneral": "Merci de remplir ce champ",
    "icoExample": "12345678",
    "icoFillRight": "est composé de 8 chiffres",
    "passwordMsg": "Le mot de passe doit contenir au moins 8 caractères",
    "phoneExample": "123 456 789",
    "phoneFillRight": "est composé de 9 chiffres",
    "pleaseFill": "Forme correcte",
    "pleaseFillRight": "Veuillez saisir la forme correcte",
    "pleaseNoSpecialChars": "Sans caractères spéciaux",
    "please_enter_the_correct_format": "Merci d’entrer le format correct",
    "pscExample": "123 45",
    "pscFillRight": "est composé de 5 chiffres",
    "streetExample": "123 Rue",
    "streetFillRight": "est le nom de la rue + le numéro de la maison",
    "variableSymbolExample": "1234567890",
    "variableSymbolFillRight": "est composé de 10 chiffres"
}
  }
</i18n>

